import { ManagedAgentResponseModel } from '@/customApiClients/mgmtApiClient/Api';
import { cn } from '@/lib/utils';
import React, { useLayoutEffect, useRef, useState } from 'react';

import { AgentDetailDialog } from '@/components/agents/AgentDetailDialog';
import { AgentDetailRow } from '@/components/agents/AgentDetailRow';
import { AgentToolsCell } from '@/components/agents/AgentToolsCell';
import { AgentAvatar } from '@/components/chat/MessageComposer/AgentAvatar';
import { AgentSourcesBadges } from '@/components/chat/MessageComposer/AgentBadges/AgentSourcesBadges';
import { MarkdownContent } from '@/components/ui/markdown-content';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';

import { useAgentInformation } from '@/hooks/useAgentInformation';

const TOGGLE_KEYS = ['Enter', ' '];

interface ManagedAgentPickerProps {
    agents: ManagedAgentResponseModel[]; // pickable agents — coordinators already filtered out by the caller
    selectedIds: string[]; // guids
    onToggle: (guid: string) => void;
}

export function ManagedAgentPicker({
    agents,
    selectedIds,
    onToggle
}: ManagedAgentPickerProps) {
    return (
        <div className="gap-3 grid grid-cols-2">
            {agents.map((agent) => (
                <ManagedAgentPickerCard
                    key={agent.guid}
                    agent={agent}
                    selected={selectedIds.includes(agent.guid)}
                    onToggle={() => onToggle(agent.guid)}
                />
            ))}
            {agents.length === 0 && (
                <p className="col-span-2 text-muted-foreground text-sm">
                    No agents found.
                </p>
            )}
        </div>
    );
}

interface ManagedAgentPickerCardProps {
    agent: ManagedAgentResponseModel;
    selected: boolean;
    onToggle: () => void;
}

function isDetailTarget(target: HTMLElement) {
    return !!target.closest('a') || !!target.closest('[data-stop-toggle]');
}

// Always visible (unlike the hover card, which defers until opened), so the
// card's info loads eagerly as soon as the agent is known.
function ManagedAgentPickerCard({
    agent,
    selected,
    onToggle
}: ManagedAgentPickerCardProps) {
    const information = useAgentInformation(agent, { lazy: false });
    const {
        loading,
        mcpServerNames,
        description,
        hasInternetAccess,
        hasCodeWriteAccess
    } = information;

    const descriptionRef = useRef<HTMLDivElement>(null);
    const [clamped, setClamped] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);

    useLayoutEffect(() => {
        const el = descriptionRef.current;
        if (!el) return;
        setClamped(el.scrollHeight > el.clientHeight + 1);
    }, [loading, description]);

    return (
        <>
            <div
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={(event) => {
                    if (isDetailTarget(event.target as HTMLElement)) return;
                    onToggle();
                }}
                onKeyDown={(event: React.KeyboardEvent) => {
                    if (!TOGGLE_KEYS.includes(event.key)) return;
                    if (isDetailTarget(event.target as HTMLElement)) return;
                    event.preventDefault();
                    onToggle();
                }}
                className={cn(
                    'flex flex-col bg-background px-3.5 py-2 border rounded-lg focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 text-left transition-colors cursor-pointer',
                    selected ? 'border-foreground' : 'border-border'
                )}
            >
                <div className="flex items-center gap-3 py-2">
                    <AgentAvatar
                        agentId={agent.agentId}
                        className="size-8"
                        animate={selected}
                    />
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                            {agent.displayName}
                        </div>
                        <div className="text-muted-foreground text-xs">
                            Agent
                        </div>
                    </div>
                    <Switch
                        checked={selected}
                        size="default"
                        tabIndex={-1}
                        className="pointer-events-none shrink-0"
                    />
                </div>

                <AgentDetailRow label="About" className="flex-1">
                    {loading ? (
                        <div className="flex flex-col gap-1.5 py-1">
                            <Skeleton className="w-full h-3" />
                            <Skeleton className="w-4/5 h-3" />
                        </div>
                    ) : (
                        <>
                            <div ref={descriptionRef} className="line-clamp-5">
                                <MarkdownContent className="text-secondary-foreground text-sm">
                                    {description}
                                </MarkdownContent>
                            </div>
                            {clamped && (
                                <button
                                    type="button"
                                    data-stop-toggle
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setDetailOpen(true);
                                    }}
                                    aria-label={`Show full description for ${agent.displayName}`}
                                    className="block mt-1 rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring ring-offset-background focus-visible:ring-offset-2 font-medium text-muted-foreground hover:text-foreground text-xs underline underline-offset-2"
                                >
                                    Show all
                                </button>
                            )}
                        </>
                    )}
                </AgentDetailRow>

                <AgentDetailRow label="Tools" alignToBadges>
                    <AgentToolsCell
                        loading={loading}
                        hasInternetAccess={hasInternetAccess}
                        hasCodeWriteAccess={hasCodeWriteAccess}
                        enableRls={agent.enableRls}
                    />
                </AgentDetailRow>

                <AgentDetailRow label="Sources" alignToBadges>
                    <div className="flex items-center min-h-7">
                        <AgentSourcesBadges
                            loading={loading}
                            mcpServerNames={mcpServerNames}
                        />
                    </div>
                </AgentDetailRow>
            </div>

            <AgentDetailDialog
                open={detailOpen}
                onOpenChange={setDetailOpen}
                agent={agent}
                information={information}
                animateAvatar={selected}
            />
        </>
    );
}
